/**
 * Where the On Body panel's photos and their placements come from.
 *
 *   - Photos: the curated preset library (`useManifest(null, "onBody")`, i.e.
 *     `GET /api/v1/presets?kind=onBody`) — in production, iJewel's photos
 *     imported with their CDN URL in `values.sourceUrl`.
 *   - Placements: `public/onbody/placements.json`, keyed by file name, carrying
 *     that same CDN URL as `source` (see `placements.ts` for the matching).
 *
 * DEV ONLY: a local session has no API server, so the library is empty. The
 * viewer's dev server publishes the 102 photos under `/onbody-test/` (outside
 * this repo — no photo is ever committed here); in a dev build, when the API
 * list is empty or the page carries `?onbodyTest=1`, the panel reads that
 * manifest instead. `import.meta.env.DEV` is a compile-time `false` in a
 * production build, so the branch — and the URL — are not in the bundle.
 */
import { useEffect, useState } from "react";
import type { ManifestEntry } from "../library/useManifest";
import { VIEWER_ORIGIN } from "../library/viewerOrigin";
import type { PlacementsFile } from "./anchorMath";
import { indexPlacements, type PlacementIndex } from "./placements";

let placementsLoad: Promise<PlacementIndex> | null = null;

/** placements.json, fetched once per page (53 KB). */
function loadPlacements(): Promise<PlacementIndex> {
  if (!placementsLoad) {
    placementsLoad = fetch("/onbody/placements.json")
      .then(async (r) => {
        if (!r.ok) throw new Error(`placements.json: HTTP ${r.status}`);
        return indexPlacements((await r.json()) as PlacementsFile);
      });
    // Forget a failure so the next mount tries again.
    placementsLoad.catch(() => {
      placementsLoad = null;
    });
  }
  return placementsLoad;
}

export function usePlacements(): PlacementIndex | null {
  const [index, setIndex] = useState<PlacementIndex | null>(null);
  useEffect(() => {
    let alive = true;
    loadPlacements()
      .then((i) => alive && setIndex(i))
      .catch(() => alive && setIndex(indexPlacements(null)));
    return () => {
      alive = false;
    };
  }, []);
  return index;
}

/** A library photo with both URLs resolved. */
export interface DevPhoto extends ManifestEntry {
  url: string;
  thumbUrl: string;
}

/** `?onbodyTest=1` asks for the test library even when the API has photos. */
export const devTestForced = (): boolean =>
  import.meta.env.DEV && new URLSearchParams(window.location.search).has("onbodyTest");

/**
 * The viewer-hosted test library — DEV builds only. Resolves to [] in
 * production and whenever `want` is false.
 */
export function useDevTestPhotos(want: boolean): DevPhoto[] {
  const [items, setItems] = useState<DevPhoto[]>([]);
  const on = import.meta.env.DEV && want;
  useEffect(() => {
    if (!on) {
      setItems([]);
      return;
    }
    let alive = true;
    const base = `${VIEWER_ORIGIN}/onbody-test`;
    fetch(`${base}/manifest.json`)
      .then((r) => (r.ok ? r.json() : []))
      .then((list: ManifestEntry[]) => {
        if (!alive || !Array.isArray(list)) return;
        setItems(
          list.map((e) => ({
            ...e,
            url: `${base}/${e.file ?? e.id}`,
            thumbUrl: `${base}/${e.thumb ?? e.file ?? e.id}`,
          })),
        );
      })
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [on]);
  return on ? items : [];
}
